import { Box, LinearProgress, Typography } from '@mui/material';
import {
  GilCodingActivity,
  GilCodingSummary,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import { IStyles } from '../../../commonUtils/styleUtils';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { GIL, GIL_CELL, gilSize, gilTableText, gilText } from './gilTheme';
import { SectionCard } from './ReportWidgets';
import { formatDuration, formatPercent } from './utils';

/**
 * The coding-telemetry sections of the GIL dashboards.
 *
 * These read the `gil.*` event stream rather than any row the project flow
 * writes, so the whole family is OPTIONAL: when the events service has nothing
 * for a project, or could not be reached, the backend leaves the field unset
 * and these components render nothing at all.
 *
 * That silence is deliberate. An empty "0 simulator runs" card is a claim - it
 * says the student never ran the simulator - and making it from missing data
 * would be worse than showing nothing.
 */

const CODING_COLOR = '#007CDC';
const HARDWARE_COLOR = '#0AA34F';
const FRICTION_COLOR = '#E6533C';

const styles: IStyles = {
  tileGrid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(4, minmax(0, 1fr))',
    },
    gap: gilSize(16, 10),
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: gilSize(4, 2),
    boxSizing: 'border-box',
    padding: gilSize(16, 12),
    border: `1px solid ${GIL.border}`,
    borderRadius: gilSize(12, 10),
    backgroundColor: GIL.surface,
    minWidth: 0,
  },
  skillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: gilSize(14, 10),
    paddingY: gilSize(7, 6),
  },
  skillLabel: {
    ...gilText(14, 600, GIL.label),
    flexBasis: { xs: pxToRem(84), md: pxToRem(110), lg: pxTovW(150) },
    flexShrink: 0,
  },
  skillBar: {
    flexGrow: 1,
    height: gilSize(9, 8),
    borderRadius: pxToRem(20),
    backgroundColor: GIL.track,
    '& .MuiLinearProgress-bar': { borderRadius: pxToRem(20) },
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: gilSize(8, 6),
  },
  chipLabel: {
    ...gilText(13, 600, GIL.label),
    marginRight: pxToRem(4),
  },
  chip: {
    borderRadius: pxToRem(20),
    paddingX: gilSize(12, 10),
    paddingY: gilSize(4, 3),
    backgroundColor: '#F4F8FC',
  },
};

interface TileProps {
  value: string;
  label: string;
  hint?: string;
  color?: string;
}

/** One figure with its label and an optional grey hint, as a bordered tile. */
export const InsightTile = ({ value, label, hint, color }: TileProps) => (
  <Box sx={styles.tile}>
    <Typography noWrap sx={gilText(24, 700, color ?? GIL.heading)}>
      {value}
    </Typography>
    <Typography sx={gilText(14, 600, GIL.label)}>{label}</Typography>
    {hint && <Typography sx={gilText(12, 500, GIL.caption)}>{hint}</Typography>}
  </Box>
);

/** A 0-100 bar with its label and value, for the skills breakdown. */
const SkillBar = ({ label, percent }: { label: string; percent: number }) => (
  <Box sx={styles.skillRow}>
    <Typography sx={styles.skillLabel}>{label}</Typography>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, percent))}
      sx={{
        ...styles.skillBar,
        '& .MuiLinearProgress-bar': { backgroundColor: CODING_COLOR },
      }}
    />
    <Typography
      sx={{ ...gilText(14, 600), minWidth: gilSize(48, 40), textAlign: 'right' }}
    >
      {formatPercent(percent)}
    </Typography>
  </Box>
);

const ratio = (part: number, whole: number) =>
  whole > 0 ? `${part}/${whole}` : '—';

/**
 * One student's coding work on one project.
 *
 * Note the two different times on show: `activeCodingTimeInSec` is tab-focused
 * editor time measured inside the frame, while the coding figure in
 * GilTimeSpent is wall clock between opening and closing the resource. They
 * answer different questions and are deliberately not reconciled - the gap
 * between them is itself informative.
 */
export const CodingActivityCard = ({
  activity,
  title = 'Coding Activity',
  subtitle,
}: {
  activity?: GilCodingActivity;
  title?: string;
  subtitle?: string;
}) => {
  if (!activity) {
    return null;
  }

  const constructs = [
    activity.usesLoop && 'Loops',
    activity.usesCondition && 'Conditions',
    activity.usesVariable && 'Variables',
    activity.usesFunction && 'Functions',
  ].filter(Boolean) as string[];

  return (
    <SectionCard
      title={title}
      subtitle={subtitle ?? 'From the Geneo IoT editor'}
    >
      <Box sx={styles.tileGrid}>
        <InsightTile
          value={formatDuration(activity.activeCodingTimeInSec)}
          label="Active Coding Time"
          hint={`${activity.editorSessionCount} editor ${
            activity.editorSessionCount === 1 ? 'session' : 'sessions'
          }`}
          color={CODING_COLOR}
        />
        <InsightTile
          value={String(activity.blockCount)}
          label="Blocks Used"
          hint={
            activity.editorMode === 'code'
              ? `${activity.codeLines} lines of code`
              : `Nesting depth ${activity.maxBlockDepth}`
          }
        />
        <InsightTile
          value={ratio(activity.simSuccessCount, activity.simRunCount)}
          label="Simulator Runs"
          hint={
            activity.simRunCount > 0
              ? `${formatDuration(activity.simTotalTimeInSec)} simulating`
              : 'Never simulated'
          }
        />
        <InsightTile
          value={activity.ranOnHardware ? 'Yes' : 'No'}
          label="Ran on Hardware"
          hint={
            activity.flashAttemptCount > 0
              ? `${ratio(
                  activity.flashSuccessCount,
                  activity.flashAttemptCount,
                )} flashes succeeded`
              : 'No device flashed'
          }
          color={activity.ranOnHardware ? HARDWARE_COLOR : undefined}
        />
      </Box>

      {constructs.length > 0 && (
        <Box sx={styles.chipRow}>
          <Typography sx={styles.chipLabel}>Concepts used:</Typography>
          {constructs.map((name) => (
            <Box key={name} sx={styles.chip}>
              <Typography sx={gilText(12, 600, CODING_COLOR)}>
                {name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {activity.errorCount > 0 && (
        <Typography sx={gilText(13, 500, FRICTION_COLOR)}>
          {`${activity.errorCount} ${
            activity.errorCount === 1 ? 'error' : 'errors'
          } shown while working`}
          {activity.topErrors.length > 0 &&
            ` — most often: ${activity.topErrors[0].errorClass}`}
        </Typography>
      )}
    </SectionCard>
  );
};

/**
 * Coding work across a group, class or school.
 *
 * `activeStudentCount` is the denominator of every average here, and it is
 * shown rather than hidden: "42 min over 18 students who coded" is a different
 * claim from "42 min over the class", and a teacher needs to know which one
 * they are reading.
 *
 * In the lab a group shares one device and one login, so that count is of the
 * logins that coded - roughly the groups - not of the children. The default
 * subtitle says so.
 */
export const CodingSummaryCard = ({
  summary,
  title = 'Coding Insights',
  subtitle,
}: {
  summary?: GilCodingSummary;
  title?: string;
  subtitle?: string;
}) => {
  if (!summary || summary.activeStudentCount === 0) {
    return null;
  }

  return (
    <SectionCard
      title={title}
      subtitle={
        subtitle ??
        `Across ${summary.activeStudentCount} coding login${
          summary.activeStudentCount === 1 ? '' : 's'
        } - in the lab a group shares one device`
      }
    >
      <Box sx={styles.tileGrid}>
        <InsightTile
          value={formatDuration(summary.avgActiveCodingTimeInSec)}
          label="Avg. Active Coding"
          hint={`${formatDuration(
            summary.totalActiveCodingTimeInSec,
          )} in total`}
          color={CODING_COLOR}
        />
        <InsightTile
          value={String(Math.round(summary.avgBlockCount))}
          label="Avg. Blocks Used"
        />
        <InsightTile
          value={
            summary.simSuccessRatePercent === undefined
              ? '—'
              : formatPercent(summary.simSuccessRatePercent)
          }
          label="Simulator Success"
          hint={`${summary.simRunCount} run${
            summary.simRunCount === 1 ? '' : 's'
          }`}
        />
        <InsightTile
          value={formatPercent(summary.ranOnHardwareStudentPercent)}
          label="Reached Hardware"
          hint={
            summary.flashSuccessRatePercent === undefined
              ? 'No device flashed'
              : `${formatPercent(
                  summary.flashSuccessRatePercent,
                )} of ${summary.flashAttemptCount} flashes`
          }
          color={HARDWARE_COLOR}
        />
        <InsightTile
          value={formatPercent(summary.simToHardwarePercent)}
          label="Simulator → Hardware"
          hint={
            summary.simToHardwarePercent === undefined
              ? 'Nobody succeeded in the simulator yet'
              : 'Of those who got it working in the simulator'
          }
          color={HARDWARE_COLOR}
        />
        <InsightTile
          value={formatPercent(summary.compileSuccessRatePercent)}
          label="Compile Success"
          hint={
            summary.compileAttemptCount > 0
              ? `${summary.compileAttemptCount} compile${
                  summary.compileAttemptCount === 1 ? '' : 's'
                } (Arduino)`
              : 'No Arduino compiles'
          }
        />
        <InsightTile
          value={
            summary.errorsPerActiveHour === undefined
              ? '—'
              : String(Math.round(summary.errorsPerActiveHour * 10) / 10)
          }
          label="Errors / Active Hour"
          hint={`${summary.errorCount} error${
            summary.errorCount === 1 ? '' : 's'
          } in total`}
        />
        <InsightTile
          value={String(summary.hardwareBlockedCount)}
          label="Stuck on Hardware"
          hint="3+ failed connects or flashes, none landed"
          color={summary.hardwareBlockedCount > 0 ? FRICTION_COLOR : undefined}
        />
      </Box>

      <Box>
        <Typography
          sx={{ ...gilText(16, 600, GIL.heading), marginBottom: gilSize(4, 2) }}
        >
          Programming concepts used
        </Typography>
        <SkillBar label="Loops" percent={summary.usesLoopStudentPercent} />
        <SkillBar
          label="Conditions"
          percent={summary.usesConditionStudentPercent}
        />
        <SkillBar
          label="Variables"
          percent={summary.usesVariableStudentPercent}
        />
        <SkillBar
          label="Functions"
          percent={summary.usesFunctionStudentPercent}
        />
      </Box>

      {summary.topErrors.length > 0 && (
        <Box sx={styles.chipRow}>
          <Typography sx={styles.chipLabel}>Most common problems:</Typography>
          {summary.topErrors.slice(0, 3).map((entry) => (
            <Box
              key={entry.errorClass}
              sx={{ ...styles.chip, backgroundColor: '#FDF0EE' }}
            >
              <Typography sx={gilText(12, 600, FRICTION_COLOR)}>
                {`${entry.errorClass} (${entry.count})`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

/**
 * The compact form for a table cell: active coding time, with a hardware tick
 * when the student got their program onto a real board.
 */
export const CodingCell = ({ activity }: { activity?: GilCodingActivity }) => {
  if (!activity) {
    return <>—</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: pxToRem(4),
      }}
    >
      <Typography noWrap sx={GIL_CELL.strong}>
        {formatDuration(activity.activeCodingTimeInSec)}
      </Typography>
      {activity.ranOnHardware && (
        <Typography
          title="Ran on hardware"
          sx={gilTableText(12, 700, HARDWARE_COLOR)}
        >
          ●
        </Typography>
      )}
    </Box>
  );
};
